import * as React from 'react'
import { Form } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { FormattedMessage, useIntl } from 'react-intl'
import 'react-datepicker/dist/react-datepicker.css'
import { TimeFormats } from '../../models/DateTime/types'
import { transformDate } from '../../models/DateTime/utils'
import { getDatePickerValues } from './getDatePickerValues'
import { DescriptionBtnWithPopup } from '../../buttons/PromptBtn/DescriptionBtnWithPopup'
import { IErrMsg } from '../types'
import { memo, useState } from 'react'
import { RequiredMark } from '../../ui/RequiredMark/RequiredMark'
import { RiskyParamUnlockBtn } from '../components/RiskyParam/RiskyParamUnlockBtn'
import { getLabel } from '../../../utils/getLabel'

export interface IDateTimePicker {
    name: string
    state: any
    valueKey: string
    messageKey: string
    schema?: any
    disabled?: boolean
    className?: string
    showTimeSelect?: boolean
    showTimeSelectOnly?: boolean
    timeIntervals?: number
    minDate?: Date
    maxDate?: Date
    timeSchema?: string
    timeFormat: string
    timeSource: string | undefined
    description?: string
    isColumn?: boolean
    isRequired?: boolean
    isLabelHidden?: boolean
    errMsg?: IErrMsg
    touched?: any
    setTouched?: (value: any) => void
    isParentDisabled?: boolean
    changeWarning?: string
    onEnter?: Function
    onBlur?: Function
    onChange?: Function

    setState(newState: any): void
}

export const DateTimePicker = memo(function DateTimePicker({
    name,
    state,
    setState,
    valueKey,
    messageKey,
    schema,
    timeSchema,
    timeSource,
    timeIntervals,
    className,
    description,
    isColumn,
    minDate,
    maxDate,
    disabled,
    isRequired,
    timeFormat,
    isLabelHidden,
    errMsg,
    touched,
    setTouched,
    isParentDisabled,
    changeWarning,
    onEnter,
    onBlur,
    onChange,
}: IDateTimePicker) {
    const intl = useIntl()
    const [isRiskyParam, setIsRiskyParam] = useState(!!changeWarning)

    const handleUnlockRiskyParam = () => setIsRiskyParam(false)

    const handleChange = (date: Date | null): void => {
        setTouched && setTouched({ ...touched, [name]: true })

        const formattedDate = transformDate(timeFormat, date, timeFormat)
        setState({ ...state, [valueKey]: formattedDate })
        if (typeof onChange === 'function') onChange(formattedDate)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (typeof onBlur === 'function') onBlur(e)
        else setTouched && setTouched({ ...touched, [name]: true })
    }

    const handleKeyDown = (evt: any) => {
        if (evt.key === 'Enter' && typeof onEnter === 'function') {
            onEnter(evt)
        }
    }

    const handleCalendarOpen = () => {
        setTimeout(() => {
            const header = document.querySelector(".react-datepicker-time__header")
            if (!header) return
            switch (timeSource) {
                case 'UTC': {
                    return header.classList.add("utc-time-header")
                }
                case 'product': {
                    return header.classList.add("product-time-header")
                }
                default: {
                    return header.classList.add("mt-time-header")
                }
            }
        }, 0)
    }

    const datepickerValues = getDatePickerValues(
        timeFormat as TimeFormats,
        state,
        valueKey
    )
    const label = getLabel(messageKey, undefined)
    const isInvalid = touched && touched[name] && errMsg

    if (!datepickerValues || datepickerValues.unsupported) {
        return (
            <div
                style={{ maxWidth: '600px' }}
                className={`${className} d-flex flex-1 mt-1 mb-1 align-items-center justify-content-between`}
            >
                <Form.Label
                    style={{ maxWidth: '30%' }}
                    className={`label-main mt-1 text-capital`}
                >
                    {label}:{' '}
                </Form.Label>
                &nbsp;Unsupported datetime/time format
            </div>
        )
    }

    return (
        <Form.Group
            style={{ maxWidth: '600px' }}
            className={`${className} d-flex flex-1 mt-1 mb-1 align-items-baseline justify-content-between`}
        >
            {!isLabelHidden ? (
                <Form.Label
                    style={{
                        maxWidth: '30%',
                        opacity: isRiskyParam ? '50%' : '100%',
                    }}
                    className={`label-main mt-1 text-capital`}
                >
                    {label}: {isRequired && <RequiredMark />}
                </Form.Label>
            ) : null}
            <div
                style={{ width: isLabelHidden ? '100%' : '70%' }}
                className={`d-flex ${!isColumn ? 'flex-1' : ''}`}
            >
                <div className="d-flex flex-column w-100 p-relative">
                    {isRiskyParam && <div className="input-overlay" />}
                    <DatePicker
                        timeCaption="Time"
                        onCalendarOpen={handleCalendarOpen}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        locale={intl.locale}
                        disabled={disabled ?? false}
                        minDate={datepickerValues.minDate}
                        maxDate={datepickerValues.maxDate}
                        minTime={datepickerValues.minTime}
                        maxTime={datepickerValues.maxTime}
                        timeIntervals={timeIntervals || 60}
                        selected={datepickerValues.selected}
                        dateFormat={datepickerValues.dateFormat}
                        timeFormat={datepickerValues.timeFormat}
                        showTimeSelect={timeFormat !== TimeFormats.DATE}
                        placeholderText={datepickerValues.placeholderText}
                        showTimeSelectOnly={datepickerValues.showTimeSelectOnly}
                        className={`form-control overflow-visible ${
                            isInvalid && !isParentDisabled && 'is-invalid'
                        }`}
                        wrapperClassName="w-100"
                        popperClassName="w-100"
                        tabIndex={isRiskyParam ? -1 : 0}
                    />
                    {description && (
                        <DescriptionBtnWithPopup
                            type={'default'}
                            header={label}
                            description={description ?? ''}
                            className="mt-1 input-tooltip"
                        />
                    )}
                    {!isParentDisabled && isInvalid && (
                        <span
                            style={{
                                color: '#dc3545',
                                marginBottom: '0',
                                fontSize: '0.7rem',
                            }}
                        >
                            <FormattedMessage id={errMsg?.message} />
                        </span>
                    )}
                </div>
                {isRiskyParam && (
                    <RiskyParamUnlockBtn
                        onUnlockConfirm={handleUnlockRiskyParam}
                        changeWarning={changeWarning}
                    />
                )}
            </div>
        </Form.Group>
    )
})
